import {
  clearHistory,
  getState,
  historyDepth,
  historyList,
  setPlayhead,
  setPlaying,
  setRecordMode,
  subscribe,
} from '../store/project';
import { ExportModal } from '../export/ExportModal';
import { buildShareUrl, importProject } from '../export/projectFile';
import { icon } from './icons';

let exportModal: ExportModal | null = null;
function openExport(): void {
  if (!exportModal) exportModal = new ExportModal();
  exportModal.open();
}

export class TopBar {
  private root: HTMLElement;
  private saveStatus!: HTMLElement;
  private playBtn!: HTMLButtonElement;
  private onPlayToggle: () => void;
  private onRecord: () => void;

  constructor(
    root: HTMLElement,
    handlers: { onPlayToggle: () => void; onRecord: () => void },
  ) {
    this.root = root;
    this.onPlayToggle = handlers.onPlayToggle;
    this.onRecord = handlers.onRecord;
    this.build();
    subscribe((s) => {
      this.playBtn.textContent = s.isPlaying ? '⏸ 정지' : '▶ 재생';
      if (s.recordMode) {
        this.saveStatus.textContent = '● 캡쳐 모드';
        this.saveStatus.classList.remove('saving');
        this.saveStatus.classList.add('record');
      } else if (s.saveBusy) {
        this.saveStatus.textContent = '저장중…';
        this.saveStatus.classList.add('saving');
        this.saveStatus.classList.remove('record');
      } else {
        this.saveStatus.textContent = '';
        this.saveStatus.classList.remove('saving', 'record');
      }
    });
  }

  private build(): void {
    while (this.root.firstChild) this.root.removeChild(this.root.firstChild);

    // ─── Action group (was right-side) — now left-aligned right after brand.
    //     "↻ 처음" moved to the timeline toolbar where it makes more sense.
    this.playBtn = button('▶ 재생', 'btn primary', () => this.onPlayToggle());

    const recordBtn = document.createElement('button');
    recordBtn.className = 'btn';
    recordBtn.title = '캡쳐 모드';
    recordBtn.appendChild(icon('record'));
    const recLabel = document.createElement('span');
    recLabel.textContent = ' 캡쳐모드';
    recordBtn.appendChild(recLabel);
    recordBtn.addEventListener('click', () => this.onRecord());

    this.root.append(this.playBtn, recordBtn);

    const exportBtn = document.createElement('button');
    exportBtn.className = 'btn ghost';
    exportBtn.title = '내보내기';
    exportBtn.appendChild(icon('export'));
    const expLabel = document.createElement('span');
    expLabel.textContent = ' 내보내기';
    expLabel.className = 'btn-label';
    exportBtn.appendChild(expLabel);
    exportBtn.addEventListener('click', () => openExport());

    const importBtn = document.createElement('button');
    importBtn.className = 'btn ghost';
    importBtn.title = '불러오기';
    importBtn.appendChild(icon('import'));
    const impLabel = document.createElement('span');
    impLabel.textContent = ' 불러오기';
    impLabel.className = 'btn-label';
    importBtn.appendChild(impLabel);
    importBtn.addEventListener('click', () => importProject());

    const shareBtn = document.createElement('button');
    shareBtn.className = 'btn ghost';
    shareBtn.title = '공유 — 이 프로젝트가 담긴 URL을 클립보드에 복사';
    shareBtn.appendChild(icon('share'));
    const shrLabel = document.createElement('span');
    shrLabel.textContent = ' 공유';
    shrLabel.className = 'btn-label';
    shareBtn.appendChild(shrLabel);
    shareBtn.addEventListener('click', async () => {
      const url = buildShareUrl(getState().project);
      try {
        await navigator.clipboard.writeText(url);
        shrLabel.textContent = ' ✓ 링크 복사됨';
        setTimeout(() => (shrLabel.textContent = ' 공유'), 1400);
      } catch {
        // Fallback: select the URL in a prompt so the user can copy manually
        window.prompt('아래 URL을 복사하세요 (⌘C / Ctrl+C):', url);
      }
    });

    // History — opens a popover listing every undo/redo step. The label
    // shows current undo depth ("히스토리 12") so the user knows the trail
    // size at a glance. Clear-all button lives inside the popover.
    const histBtn = document.createElement('button');
    histBtn.className = 'btn ghost';
    histBtn.title = '작업 히스토리 보기';
    histBtn.appendChild(icon('history'));
    const histLabel = document.createElement('span');
    histLabel.className = 'btn-label';
    histLabel.textContent = ' 히스토리';
    histBtn.appendChild(histLabel);
    const syncHistLabel = (): void => {
      const d = historyDepth();
      histLabel.textContent = d.undo + d.redo > 0 ? ` 히스토리 ${d.undo}` : ' 히스토리';
    };
    syncHistLabel();
    subscribe(syncHistLabel);
    histBtn.addEventListener('click', () => openHistoryPopover(histBtn));

    this.root.append(exportBtn, importBtn, shareBtn, histBtn);

    this.saveStatus = document.createElement('span');
    this.saveStatus.className = 'save-status';
    // Filled in by subscribe(); empty when idle, "저장중…" with spinner while
    // persisting, "● 캡쳐 모드" when recording.
    this.root.appendChild(this.saveStatus);

    // Push the privacy notice to the far right
    const spacer = document.createElement('div');
    spacer.className = 'spacer';
    this.root.appendChild(spacer);

    this.root.appendChild(buildPrivacyNotice());

    // We keep refs to the controls that StatusBar reads via subscribe(),
    // but they're built INTO the StatusBar element rather than the TopBar.
    // StatusBar is constructed separately from main.ts.
  }

  // (canvas / project controls moved to the StatusBar; background controls
  //  moved to the side popout from main.ts)
}

function buildPrivacyNotice(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'privacy-notice';

  const star = document.createElement('span');
  star.className = 'privacy-star';
  star.textContent = '*';

  const txt = document.createElement('span');
  txt.className = 'privacy-text';
  txt.textContent = '서버에 저장되지 않으니 안심하세요.';

  const help = document.createElement('button');
  help.type = 'button';
  help.className = 'privacy-help';
  help.title = '주의사항 보기';
  help.setAttribute('aria-label', '주의사항');
  help.textContent = '?';
  help.addEventListener('click', openPrivacyDialog);

  wrap.append(star, txt, help);
  return wrap;
}

function openPrivacyDialog(): void {
  const bg = document.createElement('div');
  bg.className = 'modal-bg';
  const close = (): void => bg.remove();
  bg.addEventListener('click', (e) => {
    if (e.target === bg) close();
  });

  const m = document.createElement('div');
  m.className = 'modal';

  const h = document.createElement('h2');
  h.className = 'modal-title-with-logo';
  const logoImg = document.createElement('img');
  logoImg.src = '/logo.png';
  logoImg.alt = '';
  logoImg.className = 'modal-title-logo';
  const titleText = document.createElement('span');
  titleText.textContent = '⚠ 주의사항';
  h.append(logoImg, titleText);
  m.appendChild(h);

  const p1 = document.createElement('p');
  p1.textContent =
    '이 에디터는 모든 작업을 사용자의 브라우저 안에만 저장해요. 서버에는 어떤 데이터도 보내지 않아요.';
  m.appendChild(p1);

  const ul = document.createElement('ul');
  ul.style.margin = '8px 0 8px 18px';
  ul.style.lineHeight = '1.7';
  for (const txt of [
    '브라우저 데이터(IndexedDB / 캐시)를 삭제하면 작업물이 함께 사라져요.',
    '시크릿 모드 / 다른 브라우저 / 다른 기기에선 같은 작업물이 보이지 않아요.',
    '중요한 작업물은 ‘내보내기 → .float 파일’로 저장해 두세요.',
    '확장 프로그램(광고 차단·프라이버시 등)이 IndexedDB를 차단하면 자동저장이 안 될 수 있어요.',
    'AI 어시스턴트의 API 키는 ‘새로고침해도 키 기억하기’를 켤 때만 localStorage에 보관돼요.',
    '업로드한 이미지·동영상은 base64로 IndexedDB에 들어가서 큰 파일은 용량을 빠르게 잡아먹을 수 있어요.',
  ]) {
    const li = document.createElement('li');
    li.textContent = txt;
    ul.appendChild(li);
  }
  m.appendChild(ul);

  const actions = document.createElement('div');
  actions.className = 'row-actions';
  const ok = document.createElement('button');
  ok.className = 'btn primary';
  ok.textContent = '확인';
  ok.addEventListener('click', close);
  actions.append(ok);
  m.appendChild(actions);

  bg.appendChild(m);
  document.body.appendChild(bg);
}

function button(text: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = className;
  b.textContent = text;
  b.addEventListener('click', onClick);
  return b;
}

let openHistPop: { close: () => void } | null = null;
function openHistoryPopover(anchor: HTMLElement): void {
  if (openHistPop) {
    openHistPop.close();
    openHistPop = null;
    return;
  }
  const list = historyList();
  const pop = document.createElement('div');
  pop.className = 'popover history-popover';
  pop.addEventListener('mousedown', (e) => e.stopPropagation());
  pop.addEventListener('click', (e) => e.stopPropagation());

  const title = document.createElement('div');
  title.className = 'popover-title';
  title.textContent = `작업 히스토리 — 되돌리기 ${list.undo.length} · 다시하기 ${list.redo.length}`;
  pop.appendChild(title);

  const scroll = document.createElement('div');
  scroll.className = 'history-list';

  const fmtT = (t: number): string => {
    const d = new Date(t);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  };
  const buildItem = (label: string, t: number, kind: 'redo' | 'undo'): HTMLElement => {
    const row = document.createElement('div');
    row.className = `history-item history-item--${kind}`;
    const arrow = document.createElement('span');
    arrow.className = 'history-arrow';
    arrow.textContent = kind === 'redo' ? '↷' : '↶';
    const lbl = document.createElement('span');
    lbl.className = 'history-label';
    lbl.textContent = label;
    const time = document.createElement('span');
    time.className = 'history-time';
    time.textContent = fmtT(t);
    row.append(arrow, lbl, time);
    return row;
  };

  if (list.redo.length === 0 && list.undo.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'history-empty';
    empty.textContent = '아직 작업 기록이 없어요.';
    scroll.appendChild(empty);
  }
  for (const e of list.redo) scroll.appendChild(buildItem(e.label, e.t, 'redo'));
  const cur = document.createElement('div');
  cur.className = 'history-cursor';
  cur.textContent = '— 현재 위치 —';
  scroll.appendChild(cur);
  for (let i = list.undo.length - 1; i >= 0; i -= 1) {
    const e = list.undo[i]!;
    scroll.appendChild(buildItem(e.label, e.t, 'undo'));
  }
  pop.appendChild(scroll);

  const footer = document.createElement('div');
  footer.className = 'history-footer';
  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'btn ghost';
  clearBtn.textContent = '↺ 히스토리 모두 비우기';
  clearBtn.disabled = list.undo.length + list.redo.length === 0;
  clearBtn.addEventListener('click', () => {
    if (list.undo.length + list.redo.length === 0) return;
    if (window.confirm(`되돌리기 ${list.undo.length}개 / 다시하기 ${list.redo.length}개를 모두 비울까요?\n현재 작업물은 유지돼요.`)) {
      clearHistory();
      close();
    }
  });
  footer.appendChild(clearBtn);
  pop.appendChild(footer);

  document.body.appendChild(pop);
  const r = anchor.getBoundingClientRect();
  pop.style.position = 'fixed';
  pop.style.top = `${r.bottom + 6}px`;
  const popWidth = pop.offsetWidth || 320;
  const desiredLeft = r.right - popWidth;
  pop.style.left = `${Math.max(8, Math.min(desiredLeft, window.innerWidth - popWidth - 8))}px`;

  const onDoc = (e: MouseEvent): void => {
    const t = e.target as Node;
    if (pop.contains(t) || anchor.contains(t)) return;
    close();
  };
  function close(): void {
    pop.remove();
    document.removeEventListener('mousedown', onDoc);
    openHistPop = null;
  }
  setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
  openHistPop = { close };
}

// Re-export so the wider app can access record-mode toggling cleanly from main.ts
export { setRecordMode, setPlaying, setPlayhead };
